import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { THEME_COLORS, THEMES, getLogoPath } from '../components/Constants';
import { authFetch } from '../utils/api';

// Import de TOUS les logos column
import LogoColumnBlue from '../images/logo_column_blue.png';
import LogoColumnGreen from '../images/logo_column_green.png';
import LogoColumnRed from '../images/logo_column_red.png';
import LogoColumnOrange from '../images/logo_column_orange.png';
import LogoColumnYellow from '../images/logo_column_yellow.png';

// Mapping pour le chargement dynamique du logo
const LOGO_COLUMN_IMAGES = {
  'logo_column_blue.png': LogoColumnBlue,
  'logo_column_green.png': LogoColumnGreen,
  'logo_column_red.png': LogoColumnRed,
  'logo_column_orange.png': LogoColumnOrange,
  'logo_column_yellow.png': LogoColumnYellow,
};

export default function LandingPage() {
    const navigate = useNavigate();
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];
    const SymbolImage = LOGO_COLUMN_IMAGES[getLogoPath(currentTheme, 'column')];

    // État pour stocker la configuration de la DB
    const [config, setConfig] = useState(null);

    const [contactEmail, setContactEmail] = useState("");
    const [contactMessage, setContactMessage] = useState("");
    const [contactStatus, setContactStatus] = useState({ type: "", msg: "" });
    const [isSending, setIsSending] = useState(false);

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSending(true);
        setContactStatus({ type: "", msg: "" });

        try {
            // Utilisation de ton wrapper magique : pas de JSON.stringify manuel, pas d'URL complète !
            const response = await authFetch.post("/api/public/contact", { 
                email: contactEmail, 
                message: contactMessage 
            });

            if (response.ok) {
                setContactStatus({ type: "success", msg: "Message envoyé avec succès ! Nous vous répondrons rapidement. 🎉" });
                setContactEmail("");
                setContactMessage("");
            } else {
                setContactStatus({ type: "error", msg: "Une erreur est survenue lors de l'envoi." });
            }
        } catch (err) {
            console.error("Erreur soumission contact :", err);
            setContactStatus({ type: "error", msg: "Impossible de joindre le serveur." });
        } finally {
            setIsSending(false);
        }
    };

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await authFetch.get("/api/public/config");
                if (response.ok) {
                    const data = await response.json();
                    setConfig(data);
                }
            } catch (err) {
                console.error("Erreur chargement config landing:", err);
            }
        };
        fetchConfig();
    }, []);

    // Définition exhaustive des fonctionnalités pour la comparaison
    const allFeatures = [
        { key: 'simplified', label: 'Budget Simplifié' },
        { key: 'expert', label: 'Budget Expert' },
        { key: 'multi', label: 'Multi-utilisateurs' },
        { key: 'export', label: 'Export des données' },
    ];

    const plans = [
        { 
            name: 'TRIAL', 
            price: 'Gratuit', 
            duration: `${config?.trial_duration_days || 90} jours`, 
            accounts: `${config?.max_accounts_trial || 2} comptes`,
            features: { simplified: true, expert: true, multi: true, export: false }
        },
        { 
            name: 'STANDARD', 
            price: `${config?.price_standard_monthly || 4.99}€`, 
            duration: 'Mensuel', 
            accounts: `${config?.max_accounts_standard || 5} comptes`,
            features: { simplified: true, expert: false, multi: false, export: true }
        },
        { 
            name: 'PREMIUM', 
            price: `${config?.price_premium_monthly || 9.99}€`, 
            duration: 'Mensuel', 
            accounts: 'Comptes illimités',
            features: { simplified: true, expert: true, multi: true, export: true }
        },
    ];

    const showcases = [
        {
            title: "Multi-comptes & Multi-profils",
            description: "Créez et gérez les comptes de toute la famille au même endroit. Visualisez les soldes actuels en un coup d'œil pour chaque personne gérée.",
            details: "La page des comptes : centralisation complète de vos entités bancaires.",
            icon: "👥"
        },
        {
            title: "Tableau de Bord Intelligent",
            description: "Votre centre de pilotage. Gérez vos factures, analysez vos flux via des tableaux croisés dynamiques et suivez votre solde disponible et à venir.",
            details: "La page principale : visibilité totale sur vos recettes et dépenses.",
            icon: "📊"
        },
        {
            title: "Dettes & Créances",
            description: "Ne perdez plus le fil de qui doit quoi. Suivez l'historique des remboursements et l'état des dettes pour chaque membre de votre entourage.",
            details: "La page des dettes : une gestion saine de vos prêts personnels.",
            icon: "💸"
        },
        {
            title: "Automatisation des Récurrences",
            description: "Planifiez vos loyers et abonnements une seule fois. Le système les enregistre automatiquement selon vos critères pour une comptabilité sans effort.",
            details: "La page des récurrences : gagnez du temps chaque mois.",
            icon: "🔄"
        },
        {
            title: "Budget Prévisionnel & Analyse",
            description: "Anticipez l'avenir en mode simplifié ou expert. Identifiez vos plus gros postes de dépenses et agissez là où c'est vraiment utile.",
            details: "La page des budgets : optimisez votre épargne et vos choix.",
            icon: "🎯"
        }
    ];

    return (
        <div style={styles.wrapper}>
            {/* --- NAVBAR --- */}
            <nav style={styles.nav}>
                <div style={styles.logoContainer} onClick={() => navigate('/')}>
                    <img src={SymbolImage} alt="Logo" style={styles.logoSymbol} />
                    <div style={styles.brandName}>
                        <span style={styles.brandMain}>Mon Budget</span>
                        <span style={{ color: themeColors.primary }}> App</span>
                    </div>
                </div>
                <div>
                    <button onClick={() => window.scrollTo({top: document.body.scrollHeight, behavior: 'smooth'})} style={{...styles.navBtn, background: 'none', color: '#555'}}>Contact</button>
                    <button onClick={() => navigate('/login')} style={styles.navBtn}>Connexion</button>
                    <button onClick={() => navigate('/inscription')} style={{...styles.navBtn, ...styles.navBtnPrimary, backgroundColor: themeColors.primary}}>S'inscrire</button>
                </div>
            </nav>

            {/* --- HERO SECTION --- */}
            <header style={styles.hero}>
                <h1 style={styles.heroTitle}>Reprenez le contrôle sur vos <span style={{color: themeColors.primary}}>finances</span>.</h1>
                <p style={styles.heroSubtitle}>Une gestion simplifiée, collaborative et visuelle de vos comptes personnels et familiaux.</p>
                <div style={styles.heroActions}>
                    <button onClick={() => navigate('/inscription')} style={{...styles.mainBtn, backgroundColor: themeColors.primary}}>Essai gratuit 90 jours</button>
                </div>
            </header>

            {/* --- SECTION SHOWCASE (Captures d'écran) --- */}
            <section style={styles.section}>
                <h2 style={styles.sectionTitle}>Une interface pensée pour vous</h2>
                {showcases.map((item, index) => (
                    <div key={index} style={{
                        ...styles.showcaseRow,
                        flexDirection: index % 2 === 0 ? 'row' : 'row-reverse'
                    }}>
                        <div style={styles.showcaseText}>
                            <span style={styles.showcaseIcon}>{item.icon}</span>
                            <h3>{item.title}</h3>
                            <p>{item.description}</p>
                            <small style={{color: themeColors.primary, fontWeight: '600'}}>{item.details}</small>
                        </div>
                        <div style={{...styles.imagePlaceholder, border: `2px solid ${themeColors.primary}22`}}>
                            {/* Remplacer par <img src={MaCapture} /> plus tard */}
                            <p style={{color: '#ccc'}}>Capture d'écran {item.title}</p>
                        </div>
                    </div>
                ))}
            </section>

            {/* --- SECTION TEMOIGNAGES --- */}
            <section style={{...styles.section, backgroundColor: '#f8f9fa'}}>
                <h2 style={styles.sectionTitle}>Ils utilisent Mon Budget App</h2>
                <div style={styles.testimonialGrid}>
                    <div style={styles.testimonialCard}>
                        <p>"Enfin un outil qui n'est pas une usine à gaz pour les pros. On gère le budget du foyer à deux très facilement."</p>
                        <strong>- Thomas R.</strong>
                    </div>
                    <div style={styles.testimonialCard}>
                        <p>"La page Situation m'a permis de voir que je dépensais trop en abonnements inutiles. Très visuel !"</p>
                        <strong>- Julie D.</strong>
                    </div>
                    <div style={styles.testimonialCard}>
                        <p>"La gestion des dettes entre amis après les vacances est devenue un jeu d'enfant."</p>
                        <strong>- Marc L.</strong>
                    </div>
                </div>
            </section>

            {/* --- SECTION PRICING --- */}
            <section style={{...styles.section, backgroundColor: '#f8f9fa'}}>
                <h2 style={styles.sectionTitle}>Choisissez votre liberté</h2>
                <div style={styles.pricingGrid}>
                    {plans.map(p => (
                        <div key={p.name} style={{
                            ...styles.priceCard,
                            borderTop: `5px solid ${themeColors.primary}` 
                        }}>
                            <h3 style={{ color: themeColors.primary, fontSize: '1.5rem' }}>{p.name}</h3>
                            <div style={styles.priceTag}>{p.price}</div>
                            <p style={{ color: '#666', fontWeight: 'bold' }}>{p.duration}</p>
                            
                            <hr style={styles.separator} />
                            
                            <ul style={styles.featureList}>
                                <li style={styles.featureItem}>
                                    <span style={{color: themeColors.success}}>✅</span> <strong>{p.accounts}</strong>
                                </li>
                                
                                {allFeatures.map(f => (
                                    <li key={f.key} style={{
                                        ...styles.featureItem,
                                        color: p.features[f.key] ? '#333' : '#666', // Suppression de l'effet barré
                                        fontWeight: p.features[f.key] ? '600' : 'normal'
                                    }}>
                                        <span>
                                            {p.features[f.key] ? (
                                                <span style={{color: themeColors.success}}>✅</span>
                                            ) : (
                                                <span style={{color: '#e74c3c'}}>❌</span>
                                            )}
                                        </span>
                                        {' '}{f.label}
                                    </li>
                                ))}
                            </ul>

                            <button 
                                onClick={() => navigate(`/inscription?plan=${p.name}`)} 
                                style={{ 
                                    ...styles.priceBtn, 
                                    backgroundColor: themeColors.primary,
                                    boxShadow: `0 4px 10px ${themeColors.primary}44`
                                }}
                            >
                                {p.name === 'TRIAL' ? 'Essayer gratuitement' : `S'abonner`}
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            <section style={{...styles.section, backgroundColor: '#fff', borderTop: '1px solid #eee'}}>
                <h2 style={styles.sectionTitle}>Besoin d'aide ? Contactez-nous</h2>
                <p style={{marginBottom: '30px', color: '#666'}}>Une question sur les abonnements ou un problème d'accès ? Laissez-nous un message.</p>
                
                <form onSubmit={handleContactSubmit} style={styles.contactForm}>
                    <input 
                        type="email" 
                        placeholder="Votre adresse e-mail" 
                        value={contactEmail} 
                        onChange={(e) => setContactEmail(e.target.value)} 
                        style={styles.contactInput} 
                        required 
                    />
                    <textarea 
                        placeholder="Votre message..." 
                        value={contactMessage} 
                        onChange={(e) => setContactMessage(e.target.value)} 
                        style={styles.contactTextarea} 
                        rows="5" 
                        required 
                    />
                    {contactStatus.msg && (
                        <p style={{ 
                            color: contactStatus.type === "success" ? themeColors.success : themeColors.danger,
                            fontWeight: 'bold', fontSize: '0.9rem'
                        }}>
                            {contactStatus.msg}
                        </p>
                    )}
                    <button 
                        type="submit" 
                        disabled={isSending}
                        style={{...styles.priceBtn, backgroundColor: themeColors.primary, maxWidth: '200px', margin: '0 auto'}}
                    >
                        {isSending ? "Envoi en cours..." : "Envoyer"}
                    </button>
                </form>
            </section>

            {/* --- FOOTER --- */}
            <footer style={styles.footer}>
                <div style={styles.footerLinks}>
                    <span onClick={() => navigate('/mentions-legales')} style={styles.footerLink}>Mentions Légales</span> | 
                    <span onClick={() => navigate('/cgv')} style={styles.footerLink}> CGV / CGU</span> | 
                    <span onClick={() => navigate('/confidentialite')} style={styles.footerLink}> Politique de Confidentialité</span>
                </div>
                <p>&copy; 2026 Mon Budget App - La gestion sereine pour les particuliers.</p>
            </footer>
        </div>
    );
}

const styles = {
    wrapper: { fontFamily: "'Segoe UI', Roboto, Helvetica, sans-serif", color: '#333', overflowX: 'hidden' },
    nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 50px', backgroundColor: 'rgba(255,255,255,0.98)', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' },
    logoContainer: { display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
    logoSymbol: { height: '40px', width: 'auto' },
    brandName: { fontSize: '1.3rem', fontWeight: 'bold' },
    navBtn: { marginLeft: '15px', padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600' },
    navBtnPrimary: { color: '#fff' },
    hero: { padding: '120px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #fff 0%, #f1f4f5 100%)' },
    heroTitle: { fontSize: '3.5rem', marginBottom: '20px', fontWeight: '800', lineHeight: '1.2' },
    heroSubtitle: { fontSize: '1.3rem', color: '#666', maxWidth: '800px', margin: '0 auto 40px' },
    mainBtn: { padding: '18px 45px', borderRadius: '35px', border: 'none', color: '#fff', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' },
    
    section: { padding: '100px 10%', textAlign: 'center' },
    sectionTitle: { fontSize: '2.5rem', marginBottom: '60px', fontWeight: '700' },
    
    showcaseRow: { display: 'flex', alignItems: 'center', gap: '50px', marginBottom: '100px', textAlign: 'left' },
    showcaseText: { flex: 1 },
    showcaseIcon: { fontSize: '2.5rem', marginBottom: '15px', display: 'block' },
    imagePlaceholder: { flex: 1.5, height: '350px', backgroundColor: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.05)', overflow: 'hidden' },
    
    testimonialGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' },
    testimonialCard: { padding: '40px', backgroundColor: '#fff', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', fontStyle: 'italic' },
    
    pricingGrid: { display: 'flex', gap: '30px', justifyContent: 'center', flexWrap: 'wrap', padding: '20px' },
    priceCard: { backgroundColor: '#fff', padding: '40px 30px', borderRadius: '20px', width: '320px', boxShadow: '0 15px 35px rgba(0,0,0,0.1)', textAlign: 'center', display: 'flex', flexDirection: 'column', transition: 'transform 0.3s' },
    priceTag: { fontSize: '2.8rem', fontWeight: '800', margin: '20px 0 5px 0' },
    separator: { width: '100%', border: 'none', borderBottom: '1px solid #eee', margin: '20px 0' },
    featureList: { listStyle: 'none', padding: 0, margin: '0 0 30px 0', textAlign: 'left', flexGrow: 1 },
    featureItem: { margin: '12px 0', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '10px' },
    priceBtn: { width: '100%', padding: '15px', borderRadius: '12px', border: 'none', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', transition: 'opacity 0.2s' },
    
    footer: { padding: '60px', textAlign: 'center', backgroundColor: '#222', color: '#bbb' },
    footerLink: { cursor: "pointer" },

    contactForm: { display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '500px', margin: '0 auto' },
    contactInput: { padding: '12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem', outline: 'none' },
    contactTextarea: { padding: '12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem', outline: 'none', resize: 'vertical' },
}